import type { ReactNode } from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dropdown } from "../primitives/Dropdown";
import { Input } from "../primitives/Input";
import { Table } from "../primitives/Table";
import { Checkbox } from "../primitives/Checkbox";

type Column = {
  key: string;
  label: string;
  type: "text" | "number" | "currency" | "percent" | "actions";
  width?: number;
  sortable?: boolean;
  hideable?: boolean;
};

type Row = {
  id: string;
  [key: string]: ReactNode;
};

type Action = {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
};

type DataTablePatternProps = {
  title?: string;
  columns: Column[];
  rows: Row[];
  rowActions?: Action[];
  rowEditButton?: (row: Row) => ReactNode;
  searchPlaceholder?: string;
  filters?: ReactNode;
  actions?: ReactNode;
  enableColumnVisibility?: boolean;
  sortState?: { key: string; direction: "asc" | "desc" };
  onSortChange?: (next: { key: string; direction: "asc" | "desc" }) => void;
  isLoading?: boolean;
  emptyState?: { title: string; description?: string };
  errorState?: { title: string; description?: string };
  pagination?: ReactNode;
};


export function DataTablePattern({
  title,
  columns,
  rows,
  rowActions,
  rowEditButton,
  searchPlaceholder,
  filters,
  actions,
  enableColumnVisibility = false,
  sortState,
  onSortChange,
  isLoading = false,
  emptyState,
  errorState,
  pagination,
}: DataTablePatternProps) {
  const [internalSort, setInternalSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<string[]>([]);
  const currentSort = sortState ?? internalSort;

  useEffect(() => {
    const initialVisible = columns
      .filter((column) => column.hideable !== false)
      .map((column) => column.key);
    setVisibleKeys((prev) => (prev.length ? prev : initialVisible));
  }, [columns]);

  const hasActions = rowActions?.length || rowEditButton;
  const actionColumn: Column = { key: "__actions", label: "", type: "actions", width: hasActions ? (rowEditButton ? 120 : 56) : 0, hideable: false };
  const baseColumns = hasActions
    ? columns.some((column) => column.type === "actions")
      ? columns
      : [...columns, actionColumn]
    : columns;
  const tableColumns = baseColumns.filter((column) =>
    column.type === "actions" || column.hideable === false ? true : visibleKeys.includes(column.key)
  );
  const columnAligns = tableColumns.map((column) => (column.type === "text" ? "left" : "right"));
  const columnWidths = tableColumns.map((column) => {
    if (column.width) return `${column.width}px`;
    if (column.type === "actions") {
      if (rowEditButton && rowActions?.length) return "120px";
      if (rowEditButton) return "80px";
      return "56px";
    }
    if (column.type === "currency") return "140px";
    if (column.type === "percent") return "80px";
    if (column.type === "number") return "120px";
    return "minmax(0, 1fr)";
  });
  const tableRows = getSortedRows(rows, currentSort, tableColumns).map((row) => {
    const cells = tableColumns.map((column) => {
      if (column.type === "actions") {
        return (
          <div className="flex justify-end items-center gap-2" key={`${row.id}-actions`}>
            {rowEditButton ? rowEditButton(row) : null}
            {rowActions?.length ? (
              <Dropdown
                label="⋯"
                items={rowActions.map((action) => ({
                  label: action.label,
                  disabled: action.disabled,
                  onClick: action.onClick,
                }))}
                align="right"
              />
            ) : null}
          </div>
        );
      }
      const value = row[column.key];
      if (column.type === "number" || column.type === "currency" || column.type === "percent") {
        return <span className="tabular-nums">{value}</span>;
      }
      return value;
    });
    return cells;
  });

  const headerCells = tableColumns.map((column) => {
    if (column.type === "actions") return "";
    const isSortable = column.sortable;
    const isActive = currentSort?.key === column.key;
    const icon = isSortable ? (isActive ? (currentSort?.direction === "asc" ? "▲" : "▼") : "↕") : null;
    const justify = column.type === "text" ? "justify-start" : "justify-end";
    if (!isSortable) {
      return <div className={`flex w-full items-center ${justify}`}>{column.label}</div>;
    }
    return (
      <button
        type="button"
        className={`flex w-full items-center gap-1 ${justify}`}
        onClick={() => {
          const next =
            currentSort?.key === column.key && currentSort.direction === "asc"
              ? { key: column.key, direction: "desc" as const }
              : { key: column.key, direction: "asc" as const };
          onSortChange?.(next);
          if (!onSortChange) setInternalSort(next);
        }}
      >
        <span>{column.label}</span>
        {icon ? <span className={isActive ? "text-text" : "text-muted"}>{icon}</span> : null}
      </button>
    );
  });

  return (
    <div className="rounded-md border border-border bg-surface p-4 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          {title ? <div className="text-sm font-semibold text-text">{title}</div> : null}
          {searchPlaceholder ? <Input label="Recherche" placeholder={searchPlaceholder} /> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filters}
          {actions}
          {enableColumnVisibility ? (
            <ColumnVisibilityMenu
              columns={baseColumns.filter((column) => column.type !== "actions")}
              visibleKeys={visibleKeys}
              onToggle={(key) =>
                setVisibleKeys((prev) =>
                  prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
                )
              }
            />
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-10 rounded-md border border-border bg-surface-muted" />
            ))}
          </div>
        ) : null}

        {!isLoading && errorState ? (
          <div className="rounded-md border border-border bg-surface p-6 text-sm">
            <div className="font-semibold text-danger">{errorState.title}</div>
            {errorState.description ? <div className="mt-2 text-muted">{errorState.description}</div> : null}
          </div>
        ) : null}

        {!isLoading && !errorState && !rows.length ? (
          <div className="rounded-md border border-border bg-surface p-6 text-sm">
            <div className="font-semibold text-text">{emptyState?.title ?? "Aucune donnée disponible."}</div>
            {emptyState?.description ? <div className="mt-2 text-muted">{emptyState.description}</div> : null}
          </div>
        ) : null}

        {!isLoading && !errorState && rows.length ? (
          <Table
            columns={headerCells}
            rows={tableRows}
            columnAligns={columnAligns}
            gridTemplateColumns={columnWidths.join(" ")}
          />
        ) : null}
      </div>

      {pagination ? <div className="mt-4">{pagination}</div> : null}
    </div>
  );
}

function getSortedRows(
  rows: Row[],
  sortState: { key: string; direction: "asc" | "desc" } | null,
  columns: Column[]
) {
  if (!sortState) return rows;
  const column = columns.find((col) => col.key === sortState.key);
  if (!column || !column.sortable) return rows;

  const direction = sortState.direction === "asc" ? 1 : -1;
  const getValue = (row: Row) => {
    const raw = row[column.key];
    if (typeof raw === "number") return raw;
    if (typeof raw === "string") {
      if (column.type === "currency" || column.type === "percent" || column.type === "number") {
        const parsed = Number(raw.replace(/[^0-9.-]+/g, ""));
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      return raw.toLowerCase();
    }
    return 0;
  };

  return [...rows].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);
    if (aValue < bValue) return -1 * direction;
    if (aValue > bValue) return 1 * direction;
    return 0;
  });
}

function ColumnVisibilityMenu({
  columns,
  visibleKeys,
  onToggle,
}: {
  columns: Column[];
  visibleKeys: string[];
  onToggle: (key: string) => void;
}) {
  const menuId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    const handleGlobalOpen = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      if (customEvent.detail !== menuId) {
        setOpen(false);
      }
    };
    document.addEventListener("xlflow-columns-open", handleGlobalOpen as EventListener);
    return () => {
      document.removeEventListener("xlflow-columns-open", handleGlobalOpen as EventListener);
    };
  }, [menuId]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = buttonRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - menuRect.width - 8);
    const top = rect.bottom + 6;
    setPosition({ top, left: Math.max(8, left) });
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="rounded-md border border-border bg-surface px-3 py-1 text-sm text-text"
        onClick={() => {
          const next = !open;
          if (next) document.dispatchEvent(new CustomEvent("xlflow-columns-open", { detail: menuId }));
          setOpen(next);
        }}
      >
        Colonnes
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-50 w-56 rounded-md border border-border bg-surface p-3 shadow-sm"
              style={{ top: position.top, left: position.left }}
            >
              <div className="text-xs uppercase tracking-[0.04em] text-muted font-medium">Colonnes</div>
              <div className="mt-2 grid gap-2">
                {columns.map((column) => {
                  const hideable = column.hideable !== false;
                  const isChecked = visibleKeys.includes(column.key);
                  return (
                    <div key={column.key} className="flex items-center justify-between">
                      <Checkbox
                        label={column.label}
                        checked={isChecked}
                        state={hideable ? "default" : "disabled"}
                        onChange={() => {
                          if (!hideable) return;
                          onToggle(column.key);
                        }}
                      />
                      {hideable ? (
                        <button
                          type="button"
                          className="text-xs text-muted"
                          onClick={() => onToggle(column.key)}
                        >
                          {isChecked ? "Masquer" : "Afficher"}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
